/**
 * Power-up theatrics: the spring coil, the worn jetpack with its flame, the
 * fizzle puff on a miss, and the "+3!" floating text.
 *
 * Kept out of Game.ts so the scene stays an orchestrator. All art is asset keys,
 * so Prompt 10 swaps in the real spring and jetpack with no change here.
 */
import Phaser from 'phaser';
import { ensurePlaceholderTexture } from '../../art/placeholders';
import { artScale } from '../../art/artScale';
import { FONT_FAMILY, PALETTE } from '../../types';

export const SPRING_TEAL = 0x2ec4b6;
export const JETPACK_ORANGE = 0xff8c42;

const PUFF_TEXTURE = 'fx/puff';
const SPARK_TEXTURE = 'fx/spark';

/**
 * Where the flame sits relative to the animal's feet while the jetpack is worn.
 * Prompt 9E documents the real nozzle offset; this is the one place to change it.
 */
/**
 * Where the flame spawns, derived from where the nozzle is actually drawn.
 *
 * The art puts the nozzle at (40, 70) in its 80x80 box; at JETPACK_WEAR_OFFSET
 * and scale 0.62 that lands here. The previous (-34, -26) would have needed the
 * nozzle drawn 21px outside the box, so the flame never came out of it.
 * scripts/art/interface.py derives this number and prints it on every build.
 */
export const JETPACK_NOZZLE_OFFSET = { x: -40, y: -45 } as const;

/** Where the jetpack itself rides on the animal's back. */
export const JETPACK_WEAR_OFFSET = { x: -40, y: -64 } as const;

/* Animal sits at depth 20; the coil and pack ride behind it, effects in front. */
const COIL_DEPTH = 15;
const PACK_DEPTH = 19;
const FLAME_DEPTH = 18;

/**
 * In front of the animal (Game's ANIMAL_DEPTH is 20).
 *
 * The streak sparkle is emitted at the animal's middle, so drawn behind it
 * the animal's own body hides nearly every particle.
 */
const SPARKLE_DEPTH = 21;
const EFFECT_DEPTH = 40;

export class PowerUpFx {
  private coil: Phaser.GameObjects.Image | undefined;
  private jetpack: Phaser.GameObjects.Image | undefined;
  private flame: Phaser.GameObjects.Particles.ParticleEmitter | undefined;
  private trail: Phaser.GameObjects.Particles.ParticleEmitter | undefined;
  private sparkle: Phaser.GameObjects.Particles.ParticleEmitter | undefined;

  constructor(private readonly scene: Phaser.Scene) {
    this.ensureTextures();
  }

  /* ---------------------------------------------------------------- spring */

  /** Coil appears under the animal and compresses. Resolves when fully squashed. */
  showSpring(x: number, y: number): Promise<void> {
    ensurePlaceholderTexture(this.scene, 'powerups/spring_coil');
    this.coil?.destroy();
    this.coil = this.scene.add
      .image(x, y, 'powerups/spring_coil')
      .setOrigin(0.5, 1)
      .setDepth(COIL_DEPTH)
      .setScale(0.9 * artScale(this.scene, 'powerups/spring_coil'), 0.2)
      .setAlpha(0);

    return new Promise((resolve) => {
      this.scene.tweens.add({
        targets: this.coil,
        alpha: 1,
        scaleY: 0.95,
        duration: 130,
        ease: 'Back.easeOut',
        onComplete: () => {
          this.scene.tweens.add({
            targets: this.coil,
            scaleY: 0.45,
            scaleX: 1.15,
            duration: 150,
            ease: 'Quad.easeIn',
            onComplete: () => resolve(),
          });
        },
      });
    });
  }

  /** Coil snaps back and fades as the animal leaves it behind. */
  releaseSpring(): void {
    const coil = this.coil;
    if (coil === undefined) return;
    this.coil = undefined;
    this.scene.tweens.add({
      targets: coil,
      scaleY: 1.4,
      scaleX: 0.85,
      duration: 140,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.scene.tweens.add({
          targets: coil,
          alpha: 0,
          scaleY: 0.3,
          duration: 320,
          onComplete: () => coil.destroy(),
        });
      },
    });
  }

  /* --------------------------------------------------------------- jetpack */

  /** Straps the jetpack on and lights the flame. */
  attachJetpack(x: number, y: number): void {
    ensurePlaceholderTexture(this.scene, 'powerups/jetpack_worn');
    this.jetpack?.destroy();
    this.jetpack = this.scene.add
      .image(x + JETPACK_WEAR_OFFSET.x, y + JETPACK_WEAR_OFFSET.y, 'powerups/jetpack_worn')
      .setOrigin(0.5, 0.5)
      .setDepth(PACK_DEPTH)
      .setScale(0.62 * artScale(this.scene, 'powerups/jetpack_worn'));

    this.scene.tweens.add({
      targets: this.jetpack,
      scale: { from: 0.2, to: 0.62 },
      duration: 180,
      ease: 'Back.easeOut',
    });

    this.flame = this.scene.add
      .particles(x + JETPACK_NOZZLE_OFFSET.x, y + JETPACK_NOZZLE_OFFSET.y, SPARK_TEXTURE, {
        speedY: { min: 90, max: 260 },
        speedX: { min: -40, max: 40 },
        lifespan: { min: 180, max: 420 },
        scale: { start: 1.2, end: 0 },
        quantity: 3,
        frequency: 24,
        tint: [0xffd166, JETPACK_ORANGE, 0xff6b6b],
        blendMode: Phaser.BlendModes.ADD,
      })
      .setDepth(FLAME_DEPTH);
  }

  /** Called from the flight tween so the pack and flame ride along. */
  positionJetpack(x: number, y: number): void {
    this.jetpack?.setPosition(x + JETPACK_WEAR_OFFSET.x, y + JETPACK_WEAR_OFFSET.y);
    this.flame?.setPosition(x + JETPACK_NOZZLE_OFFSET.x, y + JETPACK_NOZZLE_OFFSET.y);
  }

  /** Jetpack pops off, tumbles away and fades (CLAUDE.md: detaches and falls). */
  detachJetpack(): void {
    this.flame?.stop();
    const flame = this.flame;
    this.flame = undefined;
    if (flame !== undefined) {
      this.scene.time.delayedCall(600, () => flame.destroy());
    }

    const pack = this.jetpack;
    if (pack === undefined) return;
    this.jetpack = undefined;
    this.scene.tweens.add({
      targets: pack,
      x: pack.x - 70,
      y: pack.y + 320,
      angle: 420,
      alpha: 0,
      scale: 0.4,
      duration: 900,
      ease: 'Quad.easeIn',
      onComplete: () => pack.destroy(),
    });
  }

  /* ----------------------------------------------------------------- misc */

  /** Faint streaks behind a fast launch, standing in for the whoosh. */
  startTrail(x: number, y: number): void {
    this.stopTrail();
    this.trail = this.scene.add
      .particles(x, y, SPARK_TEXTURE, {
        speedY: { min: 30, max: 120 },
        lifespan: 260,
        scale: { start: 0.9, end: 0 },
        alpha: { start: 0.55, end: 0 },
        quantity: 2,
        frequency: 28,
        tint: [0xf7fbff, 0x8ecae6],
      })
      .setDepth(FLAME_DEPTH);
  }

  positionTrail(x: number, y: number): void {
    this.trail?.setPosition(x, y);
  }

  stopTrail(): void {
    const trail = this.trail;
    this.trail = undefined;
    if (trail === undefined) return;
    trail.stop();
    this.scene.time.delayedCall(400, () => trail.destroy());
  }

  /**
   * A gentle sparkle that follows the animal while a long streak is running.
   *
   * A separate emitter from the jetpack trail: that one belongs to a power-up
   * and ends when the flight does, while this reflects how the player is doing
   * and persists across jumps until the streak breaks.
   */
  startStreakSparkle(x: number, y: number): void {
    if (this.sparkle !== undefined) return;
    this.sparkle = this.scene.add
      // The round puff, not the thin spark bar: a 6x14 spark is three or four
      // pixels across for most of its life and simply cannot be seen.
      .particles(x, y, PUFF_TEXTURE, {
        // Slow and close, so it reads as a trail on the animal rather than as
        // weather. No white in the tints: the bright bands have a near-white
        // sky, and a white spark simply disappears into it.
        // Spawned across the animal's silhouette rather than from one point,
        // or they pile up on its face instead of sparkling around it.
        emitZone: {
          type: 'random',
          source: new Phaser.Geom.Ellipse(0, 0, 92, 116),
          quantity: 1,
        },
        speedX: { min: -26, max: 26 },
        speedY: { min: -46, max: -6 },
        lifespan: 620,
        scale: { start: 0.62, end: 0 },
        alpha: { start: 0.95, end: 0 },
        quantity: 1,
        frequency: 60,
        tint: [0xffc145, 0x2ec4b6, 0xff8fa3],
      })
      .setDepth(SPARKLE_DEPTH);
  }

  positionStreakSparkle(x: number, y: number): void {
    this.sparkle?.setPosition(x, y);
  }

  stopStreakSparkle(): void {
    const sparkle = this.sparkle;
    this.sparkle = undefined;
    if (sparkle === undefined) return;
    sparkle.stop();
    this.scene.time.delayedCall(700, () => sparkle.destroy());
  }

  /** Small puff where a bonus icon fizzles out after a miss. */
  puffAt(x: number, y: number, tint: number): void {
    const emitter = this.scene.add
      .particles(x, y, PUFF_TEXTURE, {
        speed: { min: 40, max: 130 },
        lifespan: 420,
        scale: { start: 0.8, end: 0 },
        alpha: { start: 0.8, end: 0 },
        quantity: 10,
        tint,
      })
      .setDepth(EFFECT_DEPTH);
    emitter.explode(10, x, y);
    this.scene.time.delayedCall(600, () => emitter.destroy());
  }

  /** "+3!" rising from the landing spot. */
  floatText(x: number, y: number, label: string, color: string): void {
    const text = this.scene.add
      .text(x, y, label, {
        fontFamily: FONT_FAMILY,
        fontSize: '52px',
        color,
        stroke: PALETTE.ink,
        strokeThickness: 8,
      })
      .setOrigin(0.5)
      .setDepth(EFFECT_DEPTH + 5);

    this.scene.tweens.add({
      targets: text,
      y: y - 90,
      scale: { from: 0.7, to: 1.25 },
      duration: 420,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.scene.tweens.add({
          targets: text,
          alpha: 0,
          y: y - 130,
          duration: 420,
          onComplete: () => text.destroy(),
        });
      },
    });
  }

  destroy(): void {
    this.coil?.destroy();
    this.jetpack?.destroy();
    this.flame?.destroy();
    this.trail?.destroy();
    this.sparkle?.destroy();
  }

  private ensureTextures(): void {
    if (!this.scene.textures.exists(PUFF_TEXTURE)) {
      const g = this.scene.make.graphics({ x: 0, y: 0 }, false);
      g.fillStyle(0xffffff, 1);
      g.fillCircle(8, 8, 8);
      g.generateTexture(PUFF_TEXTURE, 16, 16);
      g.destroy();
    }
    if (!this.scene.textures.exists(SPARK_TEXTURE)) {
      const g = this.scene.make.graphics({ x: 0, y: 0 }, false);
      g.fillStyle(0xffffff, 1);
      g.fillRoundedRect(0, 0, 6, 14, 3);
      g.generateTexture(SPARK_TEXTURE, 6, 14);
      g.destroy();
    }
  }
}
